/**
 * Custom react-flow edge renderer for the mesh.
 *
 * Edge styling:
 *   - delegates_to → solid line, blue, opacity from confidence (0.6→1.0)
 *   - mcp_call     → solid line, purple
 *   - a2a_call     → dashed line, orange (animated by canvas-level config)
 *
 * Observed=false edges (synthetic targets) get dashed strokes regardless
 * of type, signalling "we see the call side but didn't scan the
 * receiving side." Hover tooltip surfaces the matching signal and
 * confidence level.
 */
import {
  BaseEdge,
  EdgeLabelRenderer,
  type EdgeProps,
  getBezierPath,
} from '@xyflow/react'
import { Box, Tooltip, Typography } from '@mui/material'
import type { MeshEdge } from './types'

const EDGE_COLOR: Record<string, string> = {
  delegates_to: '#1976d2',
  mcp_call: '#7b1fa2',
  a2a_call: '#ed6c02',
  // Phase A-D dependency edges. Lighter shades so the canvas reads
  // "delegation + protocol edges first, dependencies in supporting role"
  // — the typical operator question is about delegation/blast-radius,
  // not "which agent uses which model."
  uses_model: '#90a4ae',
  uses_tool: '#00838f',
  uses_memory: '#8e24aa',
  calls_api: '#ef6c00',
  // Agent Reach. `reaches` is what an agent can touch; `feeds` is how the
  // data got there, which is how a classification travels to a store whose
  // own declaration says nothing about it.
  reaches: '#455a64',
  feeds: '#8d6e63',
}

interface MeshEdgeData extends Record<string, unknown> {
  edge: MeshEdge
  // Escalation-path UI fix (2026-07-29) — client-only, computed by
  // MeshView.tsx from an active focusPath, not part of the backend
  // MeshEdge shape. True when this edge connects two CONSECUTIVE nodes
  // in the active escalation path.
  pathHighlighted?: boolean
}

export function MeshEdgeView({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps) {
  const { edge, pathHighlighted } = data as MeshEdgeData
  const [path, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  })

  const color = EDGE_COLOR[edge.type] || '#90a4ae'
  // Confidence-based opacity: 0.6 → 0.5 alpha, 1.0 → 1.0 alpha. Stays
  // legible at low confidence without being invisible.
  const opacity = 0.5 + 0.5 * Math.max(0, Math.min(1, edge.confidence - 0.5))

  const isInferred = !edge.observed

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={`url(#mesh-arrow-${edge.type})`}
        style={
          pathHighlighted
            // Escalation-path UI fix (2026-07-29) — differentiated by
            // weight + dash pattern, not color alone (same "not by hue
            // alone" convention as the confidence-chip fix), so it stays
            // legible for colorblind users and doesn't collide with any
            // of the 7 existing EDGE_COLOR values.
            ? { stroke: '#c62828', strokeWidth: 4, strokeDasharray: '2 3', opacity: 1 }
            : {
                stroke: color,
                strokeWidth: selected ? 2.5 : 1.5,
                strokeDasharray: isInferred ? '6 4' : undefined,
                opacity,
              }
        }
      />
      <EdgeLabelRenderer>
        <Box
          sx={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <Tooltip
            placement="top"
            title={
              <Box sx={{ fontSize: 11 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  {edge.type.replace('_', ' ')}
                  {isInferred ? ' (inferred)' : ''}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Confidence: {(edge.confidence * 100).toFixed(0)}%
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Signal: {edge.detection || '—'}
                </Typography>
                {edge.transport && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Transport: {edge.transport}
                  </Typography>
                )}
                {edge.method && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Method: {edge.method}
                  </Typography>
                )}
              </Box>
            }
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: color,
                opacity,
                border: '1px solid #fff',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </Box>
      </EdgeLabelRenderer>
    </>
  )
}

/**
 * SVG arrow-marker definitions injected once at the top of the canvas.
 * One arrow color per edge type so the arrow head reads as the same
 * channel as the edge line.
 */
export function MeshArrowDefs() {
  return (
    <svg style={{ position: 'absolute', width: 0, height: 0 }}>
      <defs>
        {Object.entries(EDGE_COLOR).map(([type, color]) => (
          <marker
            key={type}
            id={`mesh-arrow-${type}`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill={color} />
          </marker>
        ))}
      </defs>
    </svg>
  )
}
